import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { useAccountingPeriods, usePeriodPreCloseCheck, useCloseAccountingPeriod, useCreateAccountingPeriod } from '../../../features/accounting/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/Dialog';
import { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { Skeleton } from '../../../components/ui/Skeleton';
import { Input, FormField } from '../../../components/ui/FormField';
import { toAppError } from '@repo/api-client';
import { getErrorMessage } from '@repo/i18n';

export const Route = createFileRoute('/_auth/accounting/periods')({
  component: PeriodsPage,
});

function PeriodsPage() {
  const { data: periods, isLoading, isError, refetch } = useAccountingPeriods();
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Accounting Periods"
        subtitle="Manage fiscal periods and closing status."
        action={<Button variant="primary" onClick={() => setIsCreateOpen(true)}>New Period</Button>}
      />
      <div className="flex-1 w-full min-h-0">
        {(() => {
          if (isLoading) return <Skeleton rows={5} />;
          if (isError) return (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load periods</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          );
          return (
            <>
              <div className="bg-surface-0 border border-surface-2 rounded-lg overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-surface-1 border-b border-surface-2">
                    <tr>
                      <th className="py-3 px-4 font-medium text-sm">Name</th>
                      <th className="py-3 px-4 font-medium text-sm">Start Date</th>
                      <th className="py-3 px-4 font-medium text-sm">End Date</th>
                      <th className="py-3 px-4 font-medium text-sm">Status</th>
                      <th className="py-3 px-4 font-medium text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periods?.map((period) => (
                      <tr key={period.id} className="border-b border-surface-2">
                        <td className="py-3 px-4 font-medium">{period.name}</td>
                        <td className="py-3 px-4">{period.startDate}</td>
                        <td className="py-3 px-4">{period.endDate}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            period.status === 'open' 
                              ? 'bg-success-100 text-success-700' 
                              : 'bg-surface-2 text-text-muted'
                          }`}>
                            {period.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {period.status === 'open' && (
                            <Button 
                              variant="ghost" size="sm" className="text-danger-600 hover:underline"
                              onClick={() => setSelectedPeriod(period.id)}
                            >
                              Close Period
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {selectedPeriod && (
                <PreCloseModal
                  periodId={selectedPeriod}
                  onClose={() => setSelectedPeriod(null)}
                />
              )}
              {isCreateOpen && (
                <CreatePeriodModal
                  isOpen={isCreateOpen}
                  onClose={() => setIsCreateOpen(false)}
                />
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}

function PreCloseModal({ periodId, onClose }: { periodId: string; onClose: () => void }) {
  const { data: checks, isLoading } = usePeriodPreCloseCheck(periodId);
  const closeMutation = useCloseAccountingPeriod(periodId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-modal">
      {isLoading ? (
        <Skeleton rows={4} />
      ) : (
        <div className="bg-surface-0 rounded-lg max-w-md w-full p-6">
          <h2 className="text-xl font-semibold mb-4">Close Accounting Period</h2>
          
          <div className="space-y-4 mb-6">
            <CheckItem label="All Journals Posted" passed={checks?.allJournalsPosted} />
            <CheckItem label="Trial Balance Balanced" passed={checks?.trialBalanceBalanced} />
            <CheckItem label="Reconciliation Complete" passed={checks?.reconciliationComplete} />

            {!checks?.readyToClose && (
              <div className="p-3 bg-danger-50 text-danger-700 rounded text-sm mt-4">
                Please resolve the failing checks above before closing the period.
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              variant="danger"
              disabled={!checks?.readyToClose || closeMutation.isPending}
              onClick={async () => {
                await closeMutation.mutateAsync();
                onClose();
              }}
            >
              {closeMutation.isPending ? 'Closing...' : 'Close Period'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckItem({ label, passed }: { label: string; passed?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span>{label}</span>
      {passed ? (
        <span className="text-success-600 font-bold">✓</span>
      ) : (
        <span className="text-danger-600 font-bold">✗</span>
      )}
    </div>
  );
}

function CreatePeriodModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateAccountingPeriod();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await createMutation.mutateAsync({ startDate, endDate });
      onClose();
    } catch (err) {
      console.error('Failed to create accounting period', err);
      setError(getErrorMessage(toAppError(err)));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md bg-surface-1">
        <DialogHeader>
          <DialogTitle>New Accounting Period</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Start Date" required htmlFor="period-start-date">
            <Input
              id="period-start-date"
              type="date"
              required
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </FormField>
          <FormField label="End Date" required htmlFor="period-end-date">
            <Input
              id="period-end-date"
              type="date"
              required
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </FormField>
          {error && <p className="text-sm text-danger-600">{error}</p>}
          <DialogFooter className="mt-6">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={createMutation.isPending}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
